/**
 * Der Knopf „Archivieren" mit der Rückfrage — geteilt zwischen den Reitern
 * *Stammdaten* aller fünf archivierbaren Arten (Grundstück, Gebäude, Einheit,
 * Mieter, Vertrag; dieselben Bezeichner `<art>-archive`,
 * `<art>-archive-confirm` / `<art>-archive-cancel` für alle fünf, Spec 02a
 * „Reiter Stammdaten", Spec 03 „Was 03 an Bestehendem ändert", Punkt 7).
 * Nur für das **nicht** archivierte Objekt — das Gegenstück für den
 * archivierten Zustand ist `ArchivedBanner`.
 *
 * Lag bis Spec 03 in `features/properties/`. Ab hier brauchen ihn drei
 * Fachbereiche (`properties`, `tenants`, `contracts`), und `features`
 * untereinander dürfen einander nicht importieren (eslint-boundaries) —
 * deshalb der Umzug nach `components/`. `components/` darf `features/`
 * selbst nicht importieren: Die `archive-preview`-Abfrage bleibt deshalb bei
 * jedem Aufrufer (`useArchivePreview()` im jeweils eigenen `api.ts` des
 * Fachbereichs) und wird hier nur als bereits instanziierte Mutation
 * hereingereicht — derselbe
 * Schnitt wie bei `Rail`, das seine Einträge auch nur entgegennimmt, statt
 * sie selbst zu laden. Bezeichner und Texte ändern sich dabei nicht.
 *
 * Lädt vor der Rückfrage `archive-preview` (Spec 02a, „Was diese Spec an
 * Feature 01 anfasst"); ist das Archivieren gesperrt, steht die Meldung des
 * Servers in der Rückfrage und der primäre Knopf ist nicht betätigbar. Nach
 * erfolgreichem Archivieren wechselt die Ansicht auf das Elternobjekt (oder
 * `/objekt`, wenn keines existiert — das gilt seit Spec 03 auch für Mieter,
 * die immer Wurzel sind) mit dem Hinweisstreifen darüber
 * (`lib/archived-notice.ts`).
 *
 * Seit Spec 02b archiviert die Kaskade den ganzen Teilbaum (ADR 0009). Der
 * mittlere Fall der Rückfrage setzt `archive-preview.summary` **wörtlich** in
 * den Satz ein — die einzige Sprachentscheidung, die im Client bleibt, ist
 * die Beugung des Verbs nach `total_count` (Spec 02b, „Oberfläche ·
 * Archivieren — Rückfrage und Hinweisstreifen"). Keine Pluralbildung aus
 * `count` + `kind.label` im Client (I9, ARCHITECTURE.md 5).
 */

import type { UseMutationResult } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import type { CSSProperties } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { components } from '@/lib/api-types'
import { ApiError } from '@/lib/api'
import type { ArchivedEntityKind } from '@/lib/archived-notice'
import { FIELD_CLASS } from '@/components/FormField'

export type ArchivePreview = components['schemas']['ArchivePreview']

/** Dieselbe Feldgestaltung wie `features/properties/fields.tsx::FIELD_CLASS`
 * — hier noch einmal lokal, weil `components/` `features/` nicht
 * importieren darf (`apps/web/CLAUDE.md`, „features kennen einander nicht" —
 * dieselbe Begründung gilt hier für `components/`, das denselben Schnitt
 * gegen `features/` zieht). Dieselbe Duplikation wie in
 * `features/objects/MoveSheet.tsx`. */
interface ArchiveControlsProps {
  readonly kind: ArchivedEntityKind
  readonly entityId: string
  readonly label: string
  readonly version: number
  /** `null` bei Grundstück und Mieter — beide sind immer Wurzel
   * (Spec 03, „Ein Mieter ist eine Wurzel"). */
  readonly parentId: string | null
  readonly onArchive: (ifMatch: string) => Promise<{ version: number }>
  readonly isArchiving: boolean
  /** Meldet Fehler an den Aufrufer, statt selbst ein zweites `<art>-edit-error`
   * ins Dokument zu setzen — die Spec kennt genau eines je Reiter
   * (Spec 02a, „Reiter Stammdaten"), das teilen sich Speichern und
   * Archivieren. */
  readonly onError: (message: string | undefined) => void
  /** Die `useArchivePreview()`-Mutation des Aufrufers — jeder der drei
   * Fachbereiche hält seine eigene, kleine Kopie in seinem `api.ts`
   * (derselbe Grund wie `ArchivePreview` als lokaler Typ dort: ein
   * eigenständiger `request()`-Aufruf statt eines verbotenen Imports aus
   * einem anderen `features/*`). */
  readonly archivePreview: UseMutationResult<ArchivePreview, unknown, string>
}

export function ArchiveControls({
  kind,
  entityId,
  label,
  version,
  parentId,
  onArchive,
  isArchiving,
  onError,
  archivePreview,
}: ArchiveControlsProps) {
  const navigate = useNavigate()

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  // „Lädt zuerst archive-preview und zeigt dann die Rückfrage" (Spec 02a,
  // „Archivieren") — die Rückfrage öffnet erst, **nachdem** die Antwort da
  // ist, nicht schon beim Klick mit nachgereichtem Inhalt. Deshalb kein
  // `AlertDialogTrigger`: Der öffnete unkontrolliert sofort.
  async function handleArchiveClick() {
    onError(undefined)
    try {
      await archivePreview.mutateAsync(entityId)
      setIsDialogOpen(true)
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  async function handleArchiveConfirm() {
    onError(undefined)
    try {
      const updated = await onArchive(String(version))
      setIsDialogOpen(false)
      const notice = { id: entityId, kind, label, version: updated.version }
      if (parentId === null) {
        await navigate({ to: '/objekt', state: { archivedNotice: notice } })
      } else {
        await navigate({
          to: '/objekt/$entityId',
          params: { entityId: parentId },
          search: { tab: undefined },
          state: { archivedNotice: notice },
        })
      }
    } catch (caught) {
      setIsDialogOpen(false)
      onError(caught instanceof ApiError ? caught.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  const preview = archivePreview.data
  const isBlocked = preview?.blocked ?? false

  // `summary` kommt fertig formuliert vom Server („3 Gebäude und 12
  // Einheiten") — die Oberfläche setzt sie nur in den Satz ein und bildet
  // keine Mehrzahl selbst. Die Verbbeugung nach `total_count` ist die einzige
  // Sprachentscheidung, die im Client bleibt (Spec 02b, „Endpunkte · Antwort
  // archive-preview").
  function confirmMessage(): string {
    if (isBlocked) return preview?.blocked_message ?? 'Archivieren ist gerade nicht möglich.'
    if (preview !== undefined && preview.summary !== null) {
      const verb = preview.total_count === 1 ? 'wird' : 'werden'
      return `${preview.summary} ${verb} mit archiviert. Alles verschwindet aus dem Baum und aus allen Listen. Wiederherstellen holt genau diese Objekte zurück.`
    }
    return 'Das Objekt verschwindet aus dem Baum und aus allen Listen. Wiederherstellen ist möglich.'
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        data-testid={`${kind}-archive`}
        disabled={archivePreview.isPending}
        aria-busy={archivePreview.isPending}
        onClick={() => void handleArchiveClick()}
        className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
      >
        Archivieren
      </button>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent
          // `zoom-in-95`/`zoom-out-95` skalieren beim Ein-/Ausblenden —
          // `design-system` verbietet Skalierung ausnahmslos. Siehe dieselbe
          // Begründung in `PropertyMasterData.tsx` (Feature 01).
          style={{ '--tw-enter-scale': 1, '--tw-exit-scale': 1 } as CSSProperties}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-section font-(--font-weight-section) text-text">
              „{label}" archivieren?
            </AlertDialogTitle>
            {/* Eigener Bezeichner: Ohne ihn ist der Meldungstext nur über die
             * Server-Antwort prüfbar, nicht über die Oberfläche — und AK-88
             * behauptet gerade über die Oberfläche etwas. Gefunden beim
             * Schreiben der Journey 02a. */}
            <AlertDialogDescription
              data-testid="archive-confirm-message"
              className="text-label text-muted"
            >
              {confirmMessage()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${kind}-archive-cancel`}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid={`${kind}-archive-confirm`}
              disabled={isBlocked || isArchiving}
              onClick={() => void handleArchiveConfirm()}
            >
              Archivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
